import { buildCommentReferenceSuggestions } from "./commentUtils";

export interface ExampleEntryCommand {
  command: string;
  text: string;
}

export interface ExampleEntry {
  title: string;
  taccounts: number;
  commands: ExampleEntryCommand[];
  finalText?: string;
}

export const exampleEntries: ExampleEntry[] = [
  {
    title: "Record Cash sales",
    taccounts: 2,
    commands: [
      {
        command: "debit cash 400 {a}",
        text: "In this example, we are posting to two accounts (cash and sales). We first will post to cash.",
      },
      {
        command: "credit sales 400 {b}",
        text: "You will notice the cash entry has a JE label of \"{a}\". Now we will post to sales.",
      },
    ],
    finalText: "Notice the JE label of \"{b}\" for the sales entry. The entry is complete.",
  },
  {
    title: "Record sales on credit",
    taccounts: 2,
    commands: [
      {
        command: "debit \"accounts receivable\" 200 {a}",
        text: "In this example, we are posting to two accounts (\"accounts receivable\" and \"sales\"). First we will post to \"accounts receivable\".",
      },
      {
        command: "credit sales 200 {a}",
        text: "Now we will post to sales and reference the existing entry.",
      },
    ],
    finalText: "You will notice that they have JE labels of {a}.1 for the \"accounts receivable\" entry and {a}.2 for the \"sales\" entry. This is because we gave the credit entry the JE label \"{a}\", which tells the system to consider it part of the first entry. You will also notice that there is no out-of-balance (debits <> credits) information. If an account name or comment has spaces, it must be enclosed in quotes. Quotes are optional for account names with no spaces. This entry is complete.",
  },
  {
    title: "Record sales and payment on account",
    taccounts: 3,
    commands: [
      {
        command: "debit cash 250 {c} \"cash received\"",
        text: "In this example, we are posting to three accounts (cash, \"accounts receivable\", and sales). We start by posting to cash with a comment and a JE reference.",
      },
      {
        command: "debit \"accounts receivable\" 150 {c} \"partial sales on credit\"",
        text: "Now we post to \"accounts receivable\" with a comment and JE reference.",
      },
      {
        command: "credit sales 400 {c} \"sale of product A13\"",
        text: "Next we post to sales with a comment and JE reference.",
      },
      {
        command: "comment {c} \"To record sales entry\"",
        text: "Now we add an overall comment for the JE.",
      },
      {
        command: "debit cash 150 {d} \"payment received\"",
        text: "Let's post to cash.",
      },
      {
        command: "credit \"accounts receivable\" 150 {d} \"payment applied\"",
        text: "Next we post again to \"accounts receivable\".",
      },
      {
        command: "comment {d} \"to record payment on account\"",
        text: "Finally, we add an overall comment.",
      },
    ],
    finalText: "Notice that the first three entries are labeled \"{c}\" and the fourth and fifth entries are labeled \"{d}\". Also notice that there are no out-of-balance (debits <> credits) entries. If an account name has spaces, it must be enclosed in quotes. We also entered line-item comments and overall comments for entries \"{c}\" and \"{d}\". Quotes are optional for account names without spaces.",
  },
  {
    title: "Record sales",
    taccounts: 3,
    commands: [
      {
        command: "debit cash 550 {a}",
        text: "In this example, we are posting three entries (cash, sales, and \"accounts receivable\"). We start by posting to cash.",
      },
      {
        command: "credit sales 750 {a}",
        text: "Now we post to sales with a label.",
      },
      {
        command: "debit \"accounts receivable\" 100 {b}",
        text: "Next we post to \"accounts receivable\".",
      },
      {
        command: "comment {a} \"To record sales paid in cash\"",
        text: "Now we add a comment using the label.",
      },
      {
        command: "comment {b} \"To record sales on credit\"",
        text: "Finally, we add another comment using the label.",
      },
    ],
    finalText: "Notice the first two entries are labeled \"{a}.1\" and \"{a}.2\". The third entry is labeled \"{b}\". You will notice that the entries do not balance, as shown under \"debits <> credits\". We also entered a comment for both the \"{a}\" line item and the \"{b}\" line item, but no overall comment.",
  },
];

export function bindExampleReferences(entry: ExampleEntry, usedReferences: Iterable<string>): ExampleEntry {
  const used = new Set(buildCommentReferenceSuggestions(Array.from(usedReferences)));
  const references = new Map<string, string>();
  const replaceReferences = (value: string): string => value.replace(/\{([a-z])\}/gu, (_match, original: string) => {
    let reference = references.get(original);
    if (!reference) {
      reference = !used.has(original)
        ? original
        : Array.from("abcdefghijklmnopqrstuvwxyz").find((letter) => !used.has(letter));
      if (!reference) throw new Error("There are not enough unused journal references to run this example.");
      references.set(original, reference);
      used.add(reference);
    }
    return reference;
  });

  return {
    ...entry,
    commands: entry.commands.map((step) => ({
      command: replaceReferences(step.command),
      text: replaceReferences(step.text),
    })),
    finalText: entry.finalText ? replaceReferences(entry.finalText) : undefined,
  };
}
